// The archivist's voice — wry, knowing, occasionally warm, never mean.
// We pick a line from the most specific bucket that matches the player's
// signal, falling back through broader buckets so every guess gets a
// reaction with some texture to it.
//
// Lines should read as a one-line marginal note from someone who knows
// the answer and is amused by the geometry of the miss.

import type { Era, RevealPayload } from "../types";

type BanterCtx = {
  km: number;
  yearsOff: number;
  era: Era;
  sameCountry: boolean;
  sameContinent: boolean;
  country: string;
  continent: string;
  scoreRatio: number; // 0..1
  roundScore: number;
};

type Pool = string[];

// ---- Pools, increasing in specificity. Lines may reference {country} /
// {continent} / {era} placeholders that we expand at pick time. -------------

const BULLSEYE: Pool = [
  "Pinpoint. The archive is yours.",
  "On the head of a pin. The dossier salutes.",
  "An astonishing fix in both axes. Sign the ledger.",
  "Co-ordinates and calendar — both correct. Rare air.",
  "Place and period, dead-on. We'll need a new map for you.",
  "Map and clock, in lockstep. The archivist tips their cap.",
  "You and the answer occupy the same square inch of history.",
  "Clean shot. The archive marks the file: SOLVED.",
  "If memory served everyone like this, we'd close the museum."
];

const PERFECT_PLACE_TIGHT_YEAR: Pool = [
  "Eagle eye. Within a stone's throw and a heartbeat.",
  "Same street, same sunrise. Excellent.",
  "Place on the dot, year nearly so. The cartographers nod.",
  "You walked into the room as the candle was being lit.",
  "Within a market stall and a season. Sharp work.",
  "Place pinned, calendar within a breath. Beautiful.",
  "The dust hadn't settled before you arrived."
];

const PERFECT_PLACE_DECADE_OFF: Pool = [
  "Right place — slightly different decade. Close enough to taste it.",
  "You found the spot. The decade was in disguise.",
  "Standing in the right square; the calendar wandered off.",
  "Bullseye on the map; the years drifted upstream.",
  "The map was solved. The clock wanted another look.",
  "Right corner of the world, slightly wrong page of history."
];

const PERFECT_PLACE_CENTURY_OFF: Pool = [
  "Right place — slightly different century. Close enough to taste it.",
  "You named the where; history changed the when on you.",
  "The map is solved. Time has its own ideas about that street.",
  "Place: settled. Calendar: still flipping pages."
];

const PERFECT_PLACE_FAR_YEAR: Pool = [
  "You found the place. The era was the puzzle.",
  "Standing in the right city, on the wrong page of the chronicle.",
  "The geography was easy; the clockwork was the trick.",
  "The map yielded. The calendar held its mystery."
];

const PERFECT_YEAR_FAR_PLACE: Pool = [
  "Nailed the year — the map needed a second look.",
  "On the dot for the year. The continent was the long way around.",
  "Calendar: surgical. Geography: scenic route.",
  "You read the year off history's face. The pin took a holiday.",
  "The clock gave up its secret early. The map kept its mouth shut."
];

const SAME_COUNTRY_TIGHT_YEAR: Pool = [
  "{country}, exactly — within a season. Solid.",
  "Right country, right calendar. The archivist signs off pleased.",
  "{country} confirmed. The year, near enough to share a drink with."
];

const SAME_COUNTRY_FAR_YEAR: Pool = [
  "{country}, exactly. The when was tougher.",
  "Right country, wrong reign. Half-credit, gracefully.",
  "{country} — but the wrong page of the dynasty.",
  "You knew the soil. The harvest was a different summer."
];

const SAME_CONTINENT_TIGHT_YEAR: Pool = [
  "Right continent, right decade. Trust the eye.",
  "Same {continent}, same era. The instinct is strong here.",
  "{continent} confirmed within a generation — that's reading the room.",
  "Half a continent off, half a generation off. Still inside the frame."
];

const SAME_CONTINENT_FAR_YEAR: Pool = [
  "Same continent — {continent}. The era slipped away.",
  "{continent}, eventually. The century took the long way home.",
  "Right continent, wrong epoch — you read the map but not the calendar.",
  "{continent} is yours. The year asked for a re-read."
];

const OFF_CONTINENT_TIGHT_YEAR: Pool = [
  "Wrong hemisphere, right heartbeat. Strange shape of a guess.",
  "The year was right. The world was just somewhere else that day.",
  "Calendar: yes. Geography: spectacularly elsewhere."
];

const WAY_OFF_BOLD: Pool = [
  "A bold guess across centuries and oceans. Bold counts.",
  "Off by a hemisphere and a millennium — but we appreciate ambition.",
  "The dossier files this one under 'noble experiments'.",
  "An honest miss that crossed two oceans. We'll allow it."
];

const WAY_OFF_GENERIC: Pool = [
  "Off by an ocean — but every guess teaches you the next one.",
  "The map disagrees. The calendar disagrees. We'll regroup.",
  "A miss this far becomes a different kind of question.",
  "Filed under 'further from the truth than the truth was from us'."
];

const CENTURY_OFF: Pool = [
  "Within a century. The map was a different story.",
  "Inside a generation or two — the geography was the trick.",
  "The right age of the world; the wrong corner of it."
];

const FALLBACK: Pool = [
  "Every miss is a calibration. Onward.",
  "Note the deviation. The next round will know more.",
  "A guess made in good faith. The archive moves on.",
  "Filed for cross-reference. Onward.",
  "The map was unkind. The calendar was no friend either.",
  "An honest attempt. We've all been there. (We have notes.)"
];

// Era-flavoured asides — appended to a base line ~30% of the time on
// medium-to-good guesses. Adds texture without overwhelming the core
// reaction.
const ERA_ASIDES: Record<Era, Pool> = {
  ancient: [
    "(The bronze speaks for itself.)",
    "(Older than most languages still spoken.)",
    "(Pre-coinage, in some cases. Bartered glory.)",
    "(Closer to mammoths than to mobile phones, in any case.)",
    "(Carved long before the wheel had rivals.)"
  ],
  medieval: [
    "(Castles, candles, and competing calendars.)",
    "(Ink was expensive; this entry survived anyway.)",
    "(A vellum-and-Latin sort of century.)",
    "(Silk-road or saddle-road, take your pick.)",
    "(Plague-adjacent, regardless of which decade.)"
  ],
  "early-modern": [
    "(Powder, print, and Pacific crossings.)",
    "(Telescopes were new; manners, optional.)",
    "(Ledgers ruled the world — and increasingly, did the ruling.)",
    "(Empires were learning each other's names — sometimes politely.)",
    "(The age of the broadsheet and the broken treaty.)"
  ],
  modern: [
    "(Rails, wires, and a rising hum.)",
    "(Photography was just learning to lie convincingly.)",
    "(The nineteenth century was crowded; the twentieth, more so.)",
    "(Within the lifetime of someone you've heard of.)",
    "(Recent enough that the silverware is still in the family.)"
  ]
};

// Flair variants for bullseye-class (always exciting). One in three reveals
// gets an exclamation flourish.
const BULLSEYE_FLOURISH: Pool = [
  " The archive applauds.",
  " Field-perfect.",
  " The dossier closes itself.",
  " A stamp in the back of the book.",
  ""
];

// ---- Selection -------------------------------------------------------------

function pick(pool: Pool, ctx: BanterCtx): string {
  if (pool.length === 0) return "";
  const idx = Math.floor(Math.random() * pool.length);
  return expand(pool[idx], ctx);
}

function expand(template: string, ctx: BanterCtx): string {
  return template
    .replace("{country}", ctx.country || "the right country")
    .replace("{continent}", ctx.continent || "the right continent")
    .replace("{era}", labelForEra(ctx.era));
}

function labelForEra(era: Era): string {
  switch (era) {
    case "ancient": return "antiquity";
    case "medieval": return "the Middle Ages";
    case "early-modern": return "the early modern era";
    case "modern": return "the modern era";
  }
}

function maybeAside(line: string, ctx: BanterCtx): string {
  // Only spice up middling-to-good lines, and only sometimes. A perfect
  // round doesn't need an aside; a zero-score doesn't deserve a joke.
  if (ctx.scoreRatio < 0.35 || ctx.scoreRatio >= 0.95) return line;
  if (Math.random() > 0.32) return line;
  const aside = pick(ERA_ASIDES[ctx.era], ctx);
  return aside ? `${line} ${aside}` : line;
}

function chooseLine(ctx: BanterCtx): string {
  const closeKm = ctx.km <= 80;
  const sameRegion = ctx.km > 80 && ctx.km <= 800;
  const wayOff = ctx.km > 8000;
  const exact = ctx.yearsOff === 0;
  const tight = ctx.yearsOff <= 5;
  const decade = ctx.yearsOff <= 25;
  const century = ctx.yearsOff <= 100;

  // Most-specific buckets first.
  if (exact && closeKm) {
    const flourish = BULLSEYE_FLOURISH[Math.floor(Math.random() * BULLSEYE_FLOURISH.length)];
    return pick(BULLSEYE, ctx) + flourish;
  }
  if (closeKm && tight) return pick(PERFECT_PLACE_TIGHT_YEAR, ctx);
  if (closeKm && decade) return pick(PERFECT_PLACE_DECADE_OFF, ctx);
  if (closeKm && century) return pick(PERFECT_PLACE_CENTURY_OFF, ctx);
  if (closeKm) return pick(PERFECT_PLACE_FAR_YEAR, ctx);

  if (exact) return pick(PERFECT_YEAR_FAR_PLACE, ctx);

  if (ctx.sameCountry && tight) return pick(SAME_COUNTRY_TIGHT_YEAR, ctx);
  if (ctx.sameCountry) return pick(SAME_COUNTRY_FAR_YEAR, ctx);

  if (ctx.sameContinent && decade) return pick(SAME_CONTINENT_TIGHT_YEAR, ctx);
  if (ctx.sameContinent) return pick(SAME_CONTINENT_FAR_YEAR, ctx);

  if (tight && sameRegion) return pick(OFF_CONTINENT_TIGHT_YEAR, ctx);

  if (wayOff && ctx.yearsOff > 500) return pick(WAY_OFF_BOLD, ctx);
  if (wayOff) return pick(WAY_OFF_GENERIC, ctx);
  if (century) return pick(CENTURY_OFF, ctx);
  return pick(FALLBACK, ctx);
}

export function composeRevealBanter(reveal: RevealPayload): string {
  const ctx: BanterCtx = {
    km: reveal.score.distanceKm,
    yearsOff: reveal.score.yearsOff,
    era: reveal.answer.era,
    sameCountry: reveal.score.sameCountry,
    sameContinent: reveal.score.sameContinent,
    country: reveal.answer.country,
    continent: reveal.score.guessContinent ?? reveal.answer.continent,
    scoreRatio: Math.max(0, Math.min(1, reveal.score.roundScore / 5000)),
    roundScore: reveal.score.roundScore
  };
  const base = chooseLine(ctx);
  return maybeAside(base, ctx);
}
